import {FC, ReactNode, useMemo, useState} from 'react';
import {Box, SxProps, Theme, Typography} from '@mui/material';
import {FormattedMessage, useIntl} from 'react-intl';
import dayjs from 'dayjs';
import {
    ArrowDownwardOutlined,
    ArrowUpwardOutlined,
    BoltOutlined,
    BusinessOutlined,
    DescriptionOutlined,
    EvStationOutlined,
    LocationOnOutlined,
    PeopleOutlineRounded,
    ReceiptLongOutlined,
    UnfoldMoreOutlined,
} from '@mui/icons-material';
import {AiAgentStructuredTableData, AiAgentTableType} from 'src/types';
import {buildCsv, downloadCsv} from '../utils/csvExport';
import {TableToolbar} from './TableToolbar';
import {tableSx} from './ChatTable.sx';

interface ChatTableProps {
    data: AiAgentStructuredTableData;
}

// --- Render-time formatters (return '—' for missing, used in table cells) ---

const formatMoney = (raw: number | undefined, currency?: string): string => {
    if (raw == null || Number.isNaN(raw)) return '—';
    const value = (raw / 10000).toFixed(2);
    return currency ? `${value} ${currency}` : value;
};

const formatDate = (iso: string | undefined): string => {
    if (!iso) return '—';
    const d = dayjs(iso);
    if (!d.isValid()) return '—';
    return d.format('DD.MM.YYYY HH:mm');
};

const formatEnergy = (wh: number | undefined): string => {
    if (wh == null || Number.isNaN(wh)) return '—';
    return `${(wh / 1000).toFixed(2)} kWh`;
};

const fullName = (row: Record<string, unknown>): string => {
    const user = row as {firstName?: string; lastName?: string; emailAddress?: string; name?: string};
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return name || user.emailAddress || user.name || '—';
};

// --- Text-value extractors (return '' for missing, used for search + CSV) ---

const asString = (v: unknown): string => (v == null || v === '' ? '' : String(v));

const toNumOrNull = (v: unknown): number | null => {
    if (v == null || v === '') return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
};

const moneyText = (raw: unknown, currency: unknown): string => {
    const n = toNumOrNull(raw);
    if (n == null) return '';
    const v = (n / 10000).toFixed(2);
    return currency ? `${v} ${asString(currency)}` : v;
};

const energyText = (wh: unknown): string => {
    const n = toNumOrNull(wh);
    if (n == null) return '';
    return `${(n / 1000).toFixed(2)} kWh`;
};

const dateText = (iso: unknown): string => {
    if (iso == null || iso === '') return '';
    const d = dayjs(String(iso));
    return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : '';
};

const dateTs = (iso: unknown): number | null => {
    if (iso == null || iso === '') return null;
    const d = dayjs(String(iso));
    return d.isValid() ? d.valueOf() : null;
};

const fullNameText = (obj: unknown): string => {
    const u = (obj ?? {}) as {firstName?: string; lastName?: string; emailAddress?: string; name?: string};
    const n = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return n || u.emailAddress || u.name || '';
};

const joinNonEmpty = (...parts: Array<string | null | undefined>): string =>
    parts.filter((p) => p != null && p !== '').join(' ').trim();

// --- Sort comparator ---

type SortValue = string | number | null;
type SortDir = 'asc' | 'desc';

interface SortState {
    key: string;
    dir: SortDir;
}

const compareSortValues = (a: SortValue, b: SortValue): number => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), undefined, {sensitivity: 'base', numeric: true});
};

// --- Column definitions ---

export interface ColumnDef {
    key: string;
    labelId: string;
    render: (row: Record<string, unknown>) => ReactNode;
    textValue: (row: Record<string, unknown>) => string;
    sortValue?: (row: Record<string, unknown>) => SortValue;
}

const InvoiceStatusChip: FC<{status?: string}> = ({status}) => {
    const label = status ?? '—';
    const chipStyle: SxProps<Theme> =
        status === 'PAID' ? {...tableSx.chip, ...tableSx.chipPaid} :
        status === 'PENDING' ? {...tableSx.chip, ...tableSx.chipPending} :
        status === 'UNPAID' ? {...tableSx.chip, ...tableSx.chipUnpaid} :
        {...tableSx.chip, ...tableSx.chipNeutral};
    return <Box component="span" sx={chipStyle}>{label}</Box>;
};

const ChargerStatusChip: FC<{status?: string}> = ({status}) => {
    const chipStyle: SxProps<Theme> =
        status === 'ONLINE' ? {...tableSx.chip, ...tableSx.chipOnline} :
        {...tableSx.chip, ...tableSx.chipOffline};
    return <Box component="span" sx={chipStyle}>{status ?? '—'}</Box>;
};

const ContractStatusChip: FC<{status?: string}> = ({status}) => {
    const chipStyle: SxProps<Theme> =
        status === 'OPERATION' ? {...tableSx.chip, ...tableSx.chipPaid} :
        status === 'INSTALLATION' ? {...tableSx.chip, ...tableSx.chipInfo} :
        status === 'TERMINATED' ? {...tableSx.chip, ...tableSx.chipUnpaid} :
        {...tableSx.chip, ...tableSx.chipNeutral};
    return <Box component="span" sx={chipStyle}>{status ?? '—'}</Box>;
};

export const columnsByType: Record<AiAgentTableType, ColumnDef[]> = {
    invoices: [
        {
            key: 'invoiceNumber',
            labelId: 'invoiceNumber',
            render: (row) => (
                <Box component="span" sx={tableSx.tdPrimary}>
                    {(row.invoiceNumber as string) ?? `#${row.id}`}
                </Box>
            ),
            textValue: (row) => asString(row.invoiceNumber) || `#${asString(row.id)}`,
        },
        {
            key: 'user',
            labelId: 'name',
            render: (row) => {
                const user = (row.user ?? {}) as Record<string, unknown>;
                return (
                    <Box sx={tableSx.cellStack}>
                        <Box component="span">{fullName(user)}</Box>
                        {user.emailAddress ? (
                            <Box component="span" sx={tableSx.cellStackSub}>{user.emailAddress as string}</Box>
                        ) : null}
                    </Box>
                );
            },
            textValue: (row) => {
                const u = (row.user ?? {}) as Record<string, unknown>;
                return joinNonEmpty(fullNameText(u), asString(u.emailAddress));
            },
        },
        {
            key: 'amount',
            labelId: 'amount',
            render: (row) => (
                <Box component="span" sx={tableSx.tdPrimary}>
                    {formatMoney(row.amount as number, row.currency as string)}
                </Box>
            ),
            textValue: (row) => moneyText(row.amount, row.currency),
            sortValue: (row) => toNumOrNull(row.amount),
        },
        {
            key: 'status',
            labelId: 'status',
            render: (row) => <InvoiceStatusChip status={row.status as string}/>,
            textValue: (row) => asString(row.status),
        },
        {
            key: 'created',
            labelId: 'createdAt',
            render: (row) => (
                <Box component="span" sx={tableSx.tdSubtle}>{formatDate(row.created as string)}</Box>
            ),
            textValue: (row) => dateText(row.created),
            sortValue: (row) => dateTs(row.created),
        },
    ],
    chargers: [
        {
            key: 'chargePointId',
            labelId: 'chargerId',
            render: (row) => (
                <Box component="span" sx={tableSx.tdPrimary}>{(row.chargePointId as string) ?? '—'}</Box>
            ),
            textValue: (row) => asString(row.chargePointId),
        },
        {
            key: 'name',
            labelId: 'name',
            render: (row) => {
                const location = (row.location ?? {}) as Record<string, unknown>;
                return (
                    <Box sx={tableSx.cellStack}>
                        <Box component="span">{(row.name as string) ?? '—'}</Box>
                        {location.name ? (
                            <Box component="span" sx={tableSx.cellStackSub}>{location.name as string}</Box>
                        ) : null}
                    </Box>
                );
            },
            textValue: (row) => {
                const loc = (row.location ?? {}) as Record<string, unknown>;
                return joinNonEmpty(asString(row.name), asString(loc.name));
            },
        },
        {
            key: 'status',
            labelId: 'status',
            render: (row) => <ChargerStatusChip status={row.currentStatus as string}/>,
            textValue: (row) => asString(row.currentStatus),
        },
        {
            key: 'vendor',
            labelId: 'vendor',
            render: (row) => (
                <Box component="span" sx={tableSx.tdSubtle}>
                    {[row.chargePointVendor, row.chargePointModel].filter(Boolean).join(' · ') || '—'}
                </Box>
            ),
            textValue: (row) =>
                [asString(row.chargePointVendor), asString(row.chargePointModel)]
                    .filter(Boolean)
                    .join(' '),
        },
        {
            key: 'lastHeartbeatTimestamp',
            labelId: 'lastHeartbeat',
            render: (row) => (
                <Box component="span" sx={tableSx.tdSubtle}>{formatDate(row.lastHeartbeatTimestamp as string)}</Box>
            ),
            textValue: (row) => dateText(row.lastHeartbeatTimestamp),
            sortValue: (row) => dateTs(row.lastHeartbeatTimestamp),
        },
    ],
    users: [
        {
            key: 'name',
            labelId: 'name',
            render: (row) => (
                <Box sx={tableSx.cellStack}>
                    <Box component="span" sx={tableSx.tdPrimary}>{fullName(row)}</Box>
                    {row.emailAddress ? (
                        <Box component="span" sx={tableSx.cellStackSub}>{row.emailAddress as string}</Box>
                    ) : null}
                </Box>
            ),
            textValue: (row) => joinNonEmpty(fullNameText(row), asString(row.emailAddress)),
        },
        {
            key: 'role',
            labelId: 'role',
            render: (row) => {
                const roles = (row.roles ?? []) as Array<{role?: string}>;
                const roleLabel = (row.userType as string) || '—';
                return (
                    <Box component="span" sx={{...tableSx.chip, ...tableSx.chipInfo}}>{roleLabel}</Box>
                );
            },
            textValue: (row) => {
                const roles = (row.roles ?? []) as Array<{role?: string}>;
                return asString(row.userType);
            },
        },
        {
            key: 'status',
            labelId: 'status',
            render: (row) => {
                const st = row.registrationStatus as string;
                const chipStyle: SxProps<Theme> = st === 'ACTIVATED'
                    ? {...tableSx.chip, ...tableSx.chipPaid}
                    : {...tableSx.chip, ...tableSx.chipNeutral};
                return <Box component="span" sx={chipStyle}>{st ?? '—'}</Box>;
            },
            textValue: (row) => asString(row.registrationStatus),
        },
        {
            key: 'lastLogin',
            labelId: 'lastLogin',
            render: (row) => (
                <Box component="span" sx={tableSx.tdSubtle}>{formatDate(row.lastLogin as string)}</Box>
            ),
            textValue: (row) => dateText(row.lastLogin),
            sortValue: (row) => dateTs(row.lastLogin),
        },
    ],
    contracts: [
        {
            key: 'name',
            labelId: 'name',
            render: (row) => (
                <Box component="span" sx={tableSx.tdPrimary}>{(row.name as string) ?? `#${row.id}`}</Box>
            ),
            textValue: (row) => asString(row.name) || `#${asString(row.id)}`,
        },
        {
            key: 'user',
            labelId: 'user',
            render: (row) => {
                const user = (row.user ?? {}) as Record<string, unknown>;
                return (
                    <Box sx={tableSx.cellStack}>
                        <Box component="span">{fullName(user)}</Box>
                        {user.emailAddress ? (
                            <Box component="span" sx={tableSx.cellStackSub}>{user.emailAddress as string}</Box>
                        ) : null}
                    </Box>
                );
            },
            textValue: (row) => {
                const u = (row.user ?? {}) as Record<string, unknown>;
                return joinNonEmpty(fullNameText(u), asString(u.emailAddress));
            },
        },
        {
            key: 'tariff',
            labelId: 'tariff',
            render: (row) => {
                const tariff = (row.tariff ?? {}) as Record<string, unknown>;
                return (
                    <Box sx={tableSx.cellStack}>
                        <Box component="span">{(tariff.name as string) ?? '—'}</Box>
                        <Box component="span" sx={tableSx.cellStackSub}>
                            {formatMoney(tariff.monthlyFee as number, tariff.currency as string)} / mo
                        </Box>
                    </Box>
                );
            },
            textValue: (row) => {
                const t = (row.tariff ?? {}) as Record<string, unknown>;
                return joinNonEmpty(asString(t.name), moneyText(t.monthlyFee, t.currency));
            },
        },
        {
            key: 'status',
            labelId: 'status',
            render: (row) => <ContractStatusChip status={row.status as string}/>,
            textValue: (row) => asString(row.status),
        },
        {
            key: 'created',
            labelId: 'createdAt',
            render: (row) => (
                <Box component="span" sx={tableSx.tdSubtle}>{formatDate(row.created as string)}</Box>
            ),
            textValue: (row) => dateText(row.created),
            sortValue: (row) => dateTs(row.created),
        },
    ],
    transactions: [
        {
            key: 'startTime',
            labelId: 'startTime',
            render: (row) => (
                <Box component="span" sx={tableSx.tdSubtle}>{formatDate(row.startTime as string)}</Box>
            ),
            textValue: (row) => dateText(row.startTime),
            sortValue: (row) => dateTs(row.startTime),
        },
        {
            key: 'user',
            labelId: 'user',
            render: (row) => {
                const user = (row.user ?? {}) as Record<string, unknown>;
                return (
                    <Box component="span">{fullName(user)}</Box>
                );
            },
            textValue: (row) => fullNameText(row.user),
        },
        {
            key: 'charger',
            labelId: 'charger',
            render: (row) => {
                const charger = (row.chargePoint ?? row.charger ?? {}) as Record<string, unknown>;
                return (
                    <Box component="span" sx={tableSx.tdSubtle}>
                        {(charger.chargePointId as string) ?? (charger.name as string) ?? '—'}
                    </Box>
                );
            },
            textValue: (row) => {
                const c = (row.chargePoint ?? row.charger ?? {}) as Record<string, unknown>;
                return asString(c.chargePointId) || asString(c.name);
            },
        },
        {
            key: 'energy',
            labelId: 'energy',
            render: (row) => (
                <Box component="span" sx={tableSx.tdPrimary}>{formatEnergy(row.energy as number)}</Box>
            ),
            textValue: (row) => energyText(row.energy),
            sortValue: (row) => toNumOrNull(row.energy),
        },
        {
            key: 'cost',
            labelId: 'cost',
            render: (row) => (
                <Box component="span" sx={tableSx.tdPrimary}>
                    {formatMoney(row.cost as number, row.currency as string)}
                </Box>
            ),
            textValue: (row) => moneyText(row.cost, row.currency),
            sortValue: (row) => toNumOrNull(row.cost),
        },
        {
            key: 'status',
            labelId: 'status',
            render: (row) => {
                const active = row.stopTime == null;
                const chipStyle: SxProps<Theme> = active
                    ? {...tableSx.chip, ...tableSx.chipOnline}
                    : {...tableSx.chip, ...tableSx.chipNeutral};
                return (
                    <Box component="span" sx={chipStyle}>
                        <FormattedMessage id={active ? 'active' : 'completed'}/>
                    </Box>
                );
            },
            textValue: (row) => (row.stopTime == null ? 'Active' : 'Completed'),
        },
    ],
    locations: [
        {
            key: 'name',
            labelId: 'name',
            render: (row) => (
                <Box component="span" sx={tableSx.tdPrimary}>{(row.name as string) ?? '—'}</Box>
            ),
            textValue: (row) => asString(row.name),
        },
        {
            key: 'city',
            labelId: 'city',
            render: (row) => {
                const address = (row.address ?? {}) as Record<string, unknown>;
                return (
                    <Box sx={tableSx.cellStack}>
                        <Box component="span">{(address.city as string) ?? '—'}</Box>
                        {address.country ? (
                            <Box component="span" sx={tableSx.cellStackSub}>{address.country as string}</Box>
                        ) : null}
                    </Box>
                );
            },
            textValue: (row) => {
                const a = (row.address ?? {}) as Record<string, unknown>;
                return joinNonEmpty(asString(a.city), asString(a.country));
            },
        },
        {
            key: 'type',
            labelId: 'locationType',
            render: (row) => (
                <Box component="span" sx={{...tableSx.chip, ...tableSx.chipInfo}}>
                    {(row.locationType as string) ?? '—'}
                </Box>
            ),
            textValue: (row) => asString(row.locationType),
        },
        {
            key: 'chargers',
            labelId: 'chargers',
            render: (row) => (
                <Box component="span" sx={tableSx.tdPrimary}>{(row.chargePointsCount as number) ?? 0}</Box>
            ),
            textValue: (row) => asString(row.chargePointsCount ?? 0),
            sortValue: (row) => toNumOrNull(row.chargePointsCount) ?? 0,
        },
        {
            key: 'clients',
            labelId: 'clients',
            render: (row) => (
                <Box component="span" sx={tableSx.tdSubtle}>{(row.clientsCount as number) ?? 0}</Box>
            ),
            textValue: (row) => asString(row.clientsCount ?? 0),
            sortValue: (row) => toNumOrNull(row.clientsCount) ?? 0,
        },
    ],
    companies: [
        {
            key: 'name',
            labelId: 'name',
            render: (row) => (
                <Box component="span" sx={tableSx.tdPrimary}>{(row.name as string) ?? '—'}</Box>
            ),
            textValue: (row) => asString(row.name),
        },
        {
            key: 'contact',
            labelId: 'contact',
            render: (row) => {
                const name = (row.contactName as string) ?? fullName(row);
                return (
                    <Box sx={tableSx.cellStack}>
                        <Box component="span">{name}</Box>
                        {row.contactEmail ? (
                            <Box component="span" sx={tableSx.cellStackSub}>{row.contactEmail as string}</Box>
                        ) : null}
                    </Box>
                );
            },
            textValue: (row) =>
                joinNonEmpty(asString(row.contactName) || fullNameText(row), asString(row.contactEmail)),
        },
        {
            key: 'city',
            labelId: 'city',
            render: (row) => {
                const address = (row.address ?? {}) as Record<string, unknown>;
                return (
                    <Box sx={tableSx.cellStack}>
                        <Box component="span">{(address.city as string) ?? '—'}</Box>
                        {address.country ? (
                            <Box component="span" sx={tableSx.cellStackSub}>{address.country as string}</Box>
                        ) : null}
                    </Box>
                );
            },
            textValue: (row) => {
                const a = (row.address ?? {}) as Record<string, unknown>;
                return joinNonEmpty(asString(a.city), asString(a.country));
            },
        },
    ],
};

const titleIconByType: Record<AiAgentTableType, ReactNode> = {
    invoices: <ReceiptLongOutlined/>,
    chargers: <EvStationOutlined/>,
    users: <PeopleOutlineRounded/>,
    contracts: <DescriptionOutlined/>,
    transactions: <BoltOutlined/>,
    locations: <LocationOnOutlined/>,
    companies: <BusinessOutlined/>,
};

export const titleLabelByType: Record<AiAgentTableType, string> = {
    invoices: 'invoices',
    chargers: 'chargers',
    users: 'users',
    contracts: 'contracts',
    transactions: 'chargingSessions',
    locations: 'locations',
    companies: 'companies',
};

export const ChatTable: FC<ChatTableProps> = ({data}) => {
    const intl = useIntl();
    const columns = columnsByType[data.type] ?? [];
    const rows = data.rows;
    const rawCount = rows?.length ?? 0;

    const [search, setSearch] = useState('');
    const [sort, setSort] = useState<SortState | null>(null);

    const filteredRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term || columns.length === 0) return rows;
        return rows.filter((row) =>
            columns.some((c) => c.textValue(row).toLowerCase().includes(term))
        );
    }, [rows, columns, search]);

    const sortedRows = useMemo(() => {
        if (!sort) return filteredRows;
        const col = columns.find((c) => c.key === sort.key);
        if (!col) return filteredRows;
        const getVal = col.sortValue ?? ((r: Record<string, unknown>) => col.textValue(r));
        const copy = [...filteredRows];
        copy.sort((a, b) => {
            const cmp = compareSortValues(getVal(a), getVal(b));
            return sort.dir === 'asc' ? cmp : -cmp;
        });
        return copy;
    }, [filteredRows, sort, columns]);

    const handleSort = (key: string): void => {
        setSort((prev) => {
            if (!prev || prev.key !== key) return {key, dir: 'asc'};
            if (prev.dir === 'asc') return {key, dir: 'desc'};
            return null;
        });
    };

    const handleExport = (): void => {
        const csvColumns = columns.map((c) => ({
            key: c.key,
            label: intl.formatMessage({id: c.labelId}),
        }));
        const csv = buildCsv(csvColumns, sortedRows, (row, key) => {
            const col = columns.find((c) => c.key === key);
            return col ? col.textValue(row) : '';
        });
        downloadCsv(intl.formatMessage({id: titleLabelByType[data.type]}), csv);
    };

    if (!rawCount) return null;

    const visibleCount = sortedRows.length;
    const isFiltered = visibleCount !== rawCount;
    const fromIdx = data.page * data.size + 1;
    const toIdx = data.page * data.size + rawCount;

    return (
        <Box sx={tableSx.root}>
            <TableToolbar
                icon={titleIconByType[data.type]}
                title={<FormattedMessage id={titleLabelByType[data.type]}/>}
                totalCount={data.totalElements}
                searchEnabled={rawCount > 0}
                searchValue={search}
                onSearchChange={setSearch}
                onExport={handleExport}
                exportDisabled={visibleCount === 0}
            />
            <Box sx={tableSx.scrollArea}>
                <Box component="table" sx={tableSx.table}>
                    <Box component="thead" sx={tableSx.thead}>
                        <Box component="tr">
                            {columns.map((col) => {
                                const isActive = sort?.key === col.key;
                                const Icon = !isActive
                                    ? UnfoldMoreOutlined
                                    : sort?.dir === 'asc'
                                        ? ArrowUpwardOutlined
                                        : ArrowDownwardOutlined;
                                return (
                                    <Box
                                        component="th"
                                        key={col.key}
                                        sx={tableSx.thSortable}
                                        onClick={() => handleSort(col.key)}
                                    >
                                        <Box component="span" sx={tableSx.thInner}>
                                            <FormattedMessage id={col.labelId}/>
                                            <Icon
                                                sx={isActive ? tableSx.sortIconActive : tableSx.sortIcon}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {visibleCount === 0 ? (
                            <Box component="tr">
                                <Box
                                    component="td"
                                    colSpan={columns.length}
                                    sx={{
                                        ...tableSx.td,
                                        ...tableSx.tdLast,
                                        textAlign: 'center' as const,
                                        color: 'neutral.400',
                                        fontSize: '0.72rem',
                                        py: 2,
                                    }}
                                >
                                    {intl.formatMessage({id: 'aiAgentTableNoResults'})}
                                </Box>
                            </Box>
                        ) : (
                            sortedRows.map((row, idx) => (
                                <Box component="tr" key={(row.id as number) ?? idx}>
                                    {columns.map((col) => (
                                        <Box
                                            component="td"
                                            key={col.key}
                                            sx={idx === visibleCount - 1 ? {...tableSx.td, ...tableSx.tdLast} : tableSx.td}
                                        >
                                            {col.render(row)}
                                        </Box>
                                    ))}
                                </Box>
                            ))
                        )}
                    </Box>
                </Box>
            </Box>
            <Box sx={tableSx.footer}>
                <Typography sx={tableSx.footerLabel}>
                    {isFiltered ? (
                        <FormattedMessage
                            id="agentTableShowingFiltered"
                            defaultMessage="Showing {visible} of {loaded} loaded"
                            values={{
                                visible: visibleCount.toLocaleString(),
                                loaded: rawCount.toLocaleString(),
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="agentTableShowing"
                            defaultMessage="Showing {from}–{to} of {total}"
                            values={{
                                from: fromIdx.toLocaleString(),
                                to: toIdx.toLocaleString(),
                                total: data.totalElements.toLocaleString(),
                            }}
                        />
                    )}
                </Typography>
            </Box>
        </Box>
    );
};
